import React, { useState } from 'react';
import { ingredients } from '../mock';
import { ArrowRight, X, Check, Sparkles } from 'lucide-react';

const Ingredients = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="ingredients" className="px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">Ingredient first</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-2">
              We put the<br />ingredients first.
            </h2>
          </div>
          <a href="#build" className="btn-chunky outline">
            See what’s inside <ArrowRight size={16} />
          </a>
        </div>

        <p className="font-brico text-lg max-w-xl mb-8 opacity-70">Tap any seed to see why it made the cut.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {ingredients.map((ing) => (
            <button
              key={ing.id}
              type="button"
              onClick={() => setSelected(ing)}
              data-testid={`ingredient-card-${ing.id}`}
              className="group relative overflow-hidden rounded-3xl crunch-shadow bg-white text-left cursor-pointer hover:-translate-y-1 transition-transform focus:outline-none focus:ring-2 focus:ring-[#E63946]"
            >
              <div className="aspect-square overflow-hidden">
                <img src={ing.image} alt={ing.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="p-4 border-t-2 border-[#1a1a1a] flex items-center justify-between">
                <span className="font-display text-lg uppercase">{ing.name}</span>
                <span className="w-3 h-3 rounded-full" style={{ background: ing.color }} />
              </div>
              <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                Tap for benefits
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <SeedModal ingredient={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
};

const SeedModal = ({ ingredient, onClose }) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="seed-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-[#FAF3E7] rounded-3xl overflow-hidden crunch-shadow border-2 border-[#1a1a1a] max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          data-testid="seed-modal-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white flex items-center justify-center crunch-shadow-sm hover:rotate-90 transition-transform"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto md:min-h-[420px] overflow-hidden" style={{ backgroundColor: ingredient.color + '33' }}>
            <img src={ingredient.image} alt={ingredient.name} className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 bg-white rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest crunch-shadow-sm">
              Real ingredient
            </div>
          </div>

          <div className="p-6 md:p-8 flex flex-col">
            <div className="text-xs font-bold uppercase tracking-widest text-[#E63946] flex items-center gap-1">
              <Sparkles size={12} /> Ingredient story
            </div>
            <h3 className="font-display text-5xl md:text-6xl uppercase leading-none mt-2">{ingredient.name}</h3>
            <p className="font-brico text-lg mt-3 opacity-80">{ingredient.tagline}</p>

            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-widest text-black/60 mb-2">Why it’s in the mix</div>
              <ul className="space-y-2">
                {ingredient.benefits?.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 font-brico">
                    <span className="mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: ingredient.color }}>
                      <Check size={12} className="text-white" strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {ingredient.fact && (
              <div className="mt-5 p-4 rounded-2xl bg-white border-2 border-[#1a1a1a]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#E63946]">Did you know?</div>
                <p className="font-brico mt-1 text-sm">{ingredient.fact}</p>
              </div>
            )}

            <button
              onClick={onClose}
              data-testid="seed-modal-done"
              className="btn-chunky mt-6 self-start"
            >
              Got it, next seed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ingredients;
